import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type RowClassParams } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import Barcode from "react-barcode";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import UnusedReturnPopup from "@user/views/inoutbound/popup_unused_return";
import PrintTrtagPopup from "@user/views/popup/popup_print_trtag";

import PrintTicket, { type PrintTicketHandle } from "@user/views/print/print_ticket";

interface State {
  gridApi: GridApi | null,
  isShowUnusedReturn: boolean,
  printTrtagInfos: any[],
  isShowPrintTrtag: boolean
}

const defState: State = {
  gridApi: null,
  isShowUnusedReturn: false,
  printTrtagInfos: [],
  isShowPrintTrtag: false
};

interface SearchParams {
  returnDateFrom: string,
  returnDateTo: string,
  trackingIdLike: string,
  pnLike: string,
  trNoLike: string,
  snLike: string,
  returnWorkerNameLike: string,
  woLike: string,
  visitNoLike: string,
  wh: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  returnDateFrom: dayjs().format("YYYYMMDD"),
  returnDateTo: dayjs().format("YYYYMMDD"),
  trackingIdLike: "",
  pnLike: "",
  trNoLike: "",
  snLike: "",
  returnWorkerNameLike: "",
  woLike: "",
  visitNoLike: "",
  wh: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "workReqStatus", type: "InvalidStatus", msg: "작업대기 상태만 등록할 수 있습니다."},
  {id: "returnYn", type: "InvalidStatus", msg: "이미 등록된 작업입니다."}
];

const validationMsg4searchBarcode = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "workReqStatus", type: "InvalidStatus", msg: "작업대기 상태만 등록할 수 있습니다."},
  {id: "returnYn", type: "InvalidStatus", msg: "이미 등록된 작업입니다."}
];

function UnusedReturn(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const printTicketRef = useRef<PrintTicketHandle>(null);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "반납일자", field: "returnDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "작업구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "상세구분", field: "inoutDetailType", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutDetailType") },
    { headerName: "출력가능", field: "", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        const printText = [];
        if (p.data.ticketFileName) printText.push("티켓");
        if (p.data.trFileName) printText.push("TR");
        return printText.length ? printText.join("/") : "불가";
      },
    },
    { headerName: "Tracking ID", field: "trackingId", width: 120, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", field: "sn", headerClass: "hd-ta-left", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]}},
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Visit No", headerClass: "hd-ta-left", field: "visitNo", width: 120, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left",  field: "ctg", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "요청자", field: "requester", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업자", field: "returnWorkerName", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab]);
  // 끝: 바코드 스캔 기능 적용

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/unused_return.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/inoutbound/unused_return", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/inoutbound/unused_return", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/inoutbound/unused_return", {});
  }

  async function addReturn() {
    openPopupUnusedReturn();
  }

  async function addReturnProc(targetInfos: any[]) {
    try {
      targetInfos[0].returnYn = "Y";
      const data = await kutil.sendPost("/api/inoutbound/unused_return.proc?mode=updateReturnYn", targetInfos, {
        validationMsg: validationMsg
      });
      await kutil.alert("등록하였습니다.");
      router.reload();
      await printTicket(targetInfos);
    } catch(e) {
      console.log(e);
    }
  }

  async function cancelReturn() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("등록취소 작업을 선택해 주세요.");
      return;
    }

    for (const info of selectedInfos) {
      info.returnYn = "N";
    }

    if (await kutil.confirm("등록취소하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/unused_return.proc?mode=updateReturnYn", selectedInfos, {
        validationMsg: validationMsg
      });
      await kutil.alert("등록취소하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function printTrtag() {
    openPopupPrintTrtag();
  }

  async function printTicket(targetInfos?: any[]) {
    const selectedInfos = targetInfos ?? state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("티켓 출력 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => !item.ticketFileName);
    if (infos.length > 0) {
      await kutil.alert("티켓 파일이 있는 작업만 출력할 수 있습니다.");
      return;
    }

    await printTicketRef.current?.print({
      infos: selectedInfos,
      ticketType: "1",
    });
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/inoutbound/unused_return.download?mode=search", searchParams, {
        "fileName": "미사용반납관리_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupUnusedReturn() {
    setState(prev => ({ ...prev, isShowUnusedReturn: true }));
  }

  async function closePopupUnusedReturn(data: any) {
    setState(prev => ({ ...prev, isShowUnusedReturn: false }));

    if (data?.isSuccess) {
      await printTicket(data.infos);
      router.reload();
    }
  }

  async function openPopupPrintTrtag() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("TR Tag 출력 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => !item.trFileName);
    if (infos.length > 0) {
      await kutil.alert("TR Tag 파일이 있는 작업만 출력할 수 있습니다.");
      return;
    }

    setState(prev => ({ ...prev, isShowPrintTrtag: true, printTrtagInfos: selectedInfos}));
  }

  async function closePopupPrintTrtag(_data: any) {
    setState(prev => ({ ...prev, isShowPrintTrtag: false }));
  }

  async function onBarcodeScan(code: string) {
    const data = await kutil.sendGet("/api/inoutbound/unused_return.data?mode=searchDetail", {trackingId: code}, {});
    if (data.infos.length === 0) {
      kutil.alert("이미 등록된 Tracking ID입니다.");
    } else if (data.infos.length === 1) {
      addReturnProc(data.infos);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>미사용 반납 관리</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printTrtag}>TR Tag 출력</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={() => printTicket()}>티켓 출력</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={cancelReturn}>등록취소</Button>
            }
            { auth.write &&
            <Button variant="contained" onClick={addReturn}>등록</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div className="date-range">
            <p>반납일자</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.returnDateFrom)}
                  onChange={(e) => {doSearch(e, {returnDateFrom: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  maxDate={dayjs(searchParams.returnDateTo)}
                />
                -
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.returnDateTo)}
                  onChange={(e) => {doSearch(e, {returnDateTo: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  minDate={dayjs(searchParams.returnDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div>
            <p>Tracking ID</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.trackingIdLike}
              onChange={(e) => { updateSearchParams({ trackingIdLike: e.target.value }) }}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="Tracking ID"
            />
          </div>
          <div>
            <p>P/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.pnLike}
              onChange={(e) => { updateSearchParams({ pnLike: e.target.value }) }}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="P/N"
            />
          </div>
          <div>
            <p>TR No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.trNoLike}
              onChange={(e) => { updateSearchParams({ trNoLike: e.target.value }) }}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="TR No"
            />
          </div>
          <div>
            <p>S/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.snLike}
              onChange={(e) => { updateSearchParams({ snLike: e.target.value }) }}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="S/N"
            />
          </div>
          <div>
            <p>작업자</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.returnWorkerNameLike}
              onChange={(e) => { updateSearchParams({ returnWorkerNameLike: e.target.value }) }}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="작업자"
            />
          </div>
          <div>
            <p>W/O</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.woLike}
              onChange={(e) => { updateSearchParams({ woLike: e.target.value }) }}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="W/O"
            />
          </div>
          <div>
            <p>Visit No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.visitNoLike}
              onChange={(e) => { updateSearchParams({ visitNoLike: e.target.value }) }}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="Visit No"
            />
          </div>
          <div>
            <p>창고</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.wh}
                onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.wh as any[]).filter(c => c.code !== "B").map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 미사용 반납 목록 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {state.isShowUnusedReturn && <UnusedReturnPopup isShow={state.isShowUnusedReturn} onClose={closePopupUnusedReturn} id={props.id} />}
        {state.isShowPrintTrtag && <PrintTrtagPopup isShow={state.isShowPrintTrtag} onClose={closePopupPrintTrtag} id={props.id} data={{infos: state.printTrtagInfos}} />}
        <PrintTicket ref={printTicketRef} />
        {/*// 미사용 반납 목록 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(UnusedReturn);